<script lang="ts">
	/**
	 * The link to a credit note's document, wherever a credit note is named (Design §4.6).
	 *
	 * One component for the customer's own list and for the two support screens, for the reason
	 * `InvoiceDocumentLinks` is one component for the archive and the subscription page: the rules
	 * are small, and a second copy of them is a second copy that drifts.
	 *
	 * - **It is a fetch, not a navigation.** Both endpoints are token-authenticated and the token
	 *   travels in a header, so a plain `<a href>` reaches them signed out and gets a 401 — and the
	 *   alternative, a token in the URL, hands it to every referrer log in between. The object URL
	 *   is deliberately not revoked straight away: Safari cancels a navigation to a blob that has
	 *   already been released, and the browser reclaims it when the tab closes.
	 * - **Which endpoint is a prop, not a guess.** The customer's copy resolves ownership through
	 *   the caller's own customer; support's does not and is `adminAuth`. Picking between them by
	 *   inspecting the route would work until somebody rendered a customer list inside an admin
	 *   page, at which point it would 404 for the admin and look like a missing document.
	 * - **The number is the title**, so somebody looking for one credit note among several can see
	 *   which is which without opening them.
	 */

	import { fetchCreditNoteDocument, fetchAdminCreditNoteDocument } from '../AppNetwork';
	import { toastError } from '../stores/toast';

	export let uuid: string;
	export let documentNumber: string;
	/** `true` on the two support screens, which read through the admin endpoint. */
	export let admin: boolean = false;
	/** What the control says. The tables that already have a Document column want just the number. */
	export let label: string = 'Credit note PDF';

	/** Per instance, so one row's spinner does not blank its neighbours. */
	let downloading: boolean = false;

	async function fnOpen(): Promise<void> {
		downloading = true;
		try {
			const blob = admin
				? await fetchAdminCreditNoteDocument(uuid)
				: await fetchCreditNoteDocument(uuid);
			window.open(URL.createObjectURL(blob), '_blank', 'noopener,noreferrer');
		} catch (error) {
			toastError('The credit note could not be opened.', (error as Error).message);
		} finally {
			downloading = false;
		}
	}
</script>

<button
	type="button"
	class="text-primary-500 hover:underline disabled:opacity-50"
	title={`Credit note ${documentNumber}`}
	disabled={downloading}
	on:click={() => void fnOpen()}
>
	{downloading ? 'Opening…' : label}
</button>
